import { z } from 'zod';
import { toAssetId, toUploadId } from '@/shared/types/common.types';
import { tolerantEnum, v1DateSchema } from '@/shared/utils/api/serverPrimitives';
import { ASSET_STATUSES, UPLOAD_STATUSES } from '../types/upload.types';
import type { SignedPartUrl, StoredAsset, UploadSession } from '../types/upload.types';

/**
 * The wire contract for `/api/v1/projects/{projectId}/uploads` and `.../assets`, from
 * Appendix A of the plan in Conbent's `docs/features/project-documents-implementation.md`.
 */
export const uploadSessionSchema = z.object({
  uploadId: z.string().uuid(),
  assetId: z.string().uuid(),
  status: tolerantEnum(UPLOAD_STATUSES),
  partSizeBytes: z.number().int().positive(),
  partCount: z.number().int().positive(),
  uploadedParts: z.array(
    z.object({
      partNumber: z.number().int().positive(),
      etag: z.string().min(1),
      sizeBytes: z.number().int().nonnegative(),
    })
  ),
  expiresAt: v1DateSchema,
});

export const partUrlsSchema = z.object({
  parts: z.array(
    z.object({ partNumber: z.number().int().positive(), url: z.string().url(), expiresAt: v1DateSchema })
  ),
});

export const assetSchema = z.object({
  assetId: z.string().uuid(),
  status: tolerantEnum(ASSET_STATUSES),
  rejectionCode: z.string().nullable(),
  fileName: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  detectedContentType: z.string().nullable(),
  sha256: z.string().nullable(),
});

export function toUploadSession(raw: z.output<typeof uploadSessionSchema>): UploadSession {
  return { ...raw, uploadId: toUploadId(raw.uploadId), assetId: toAssetId(raw.assetId) };
}

export function toSignedPartUrls(raw: z.output<typeof partUrlsSchema>): readonly SignedPartUrl[] {
  return raw.parts;
}

export function toStoredAsset(raw: z.output<typeof assetSchema>): StoredAsset {
  return { ...raw, assetId: toAssetId(raw.assetId) };
}
